// Radha — dispute SLA policy (Zoho Desk style). A resolution target in days per
// dispute; high-value cases get a tighter clock. Pure read-side over raised_on /
// resolved_on — no schema. Used to age open disputes and score resolved ones.
import type { Dispute } from "../hooks/useDisputesDirect";

const DAY = 86_400_000;
const OPEN = new Set(["open", "under_review", "escalated"]);

/** Resolution SLA target in days. High-value disputes are held to a tighter clock. */
export function slaTargetDays(d: Dispute): number {
  return d.is_high_value ? 7 : 15;
}

const daysBetween = (fromISO: string, toMs: number) =>
  Math.floor((toMs - new Date(fromISO).getTime()) / DAY);

export interface SlaState {
  open: boolean;
  target: number;
  /** open only: days remaining (negative = overdue) */
  remaining?: number;
  breached?: boolean;
  /** resolved only: was it resolved within target */
  met?: boolean;
  label: string;
  color: string;
}

export function slaState(d: Dispute, nowMs: number): SlaState {
  const target = slaTargetDays(d);
  if (d.status === "resolved") {
    const met = d.resolved_on ? daysBetween(d.raised_on, new Date(d.resolved_on).getTime()) <= target : true;
    return { open: false, target, met, label: met ? "Met" : "Missed", color: met ? "var(--status-good)" : "var(--status-critical)" };
  }
  if (!OPEN.has(d.status)) return { open: false, target, label: "—", color: "var(--fg-muted)" };

  const age = daysBetween(d.raised_on, nowMs);
  const remaining = target - age;
  if (remaining < 0) return { open: true, target, remaining, breached: true, label: `${-remaining}d over`, color: "var(--status-critical)" };
  if (remaining <= 2) return { open: true, target, remaining, breached: false, label: `due ${remaining}d`, color: "var(--status-warning)" };
  return { open: true, target, remaining, breached: false, label: `due ${remaining}d`, color: "var(--status-good)" };
}

/** Portfolio SLA rollup: open breaches, and resolved-within-SLA compliance. */
export function slaSummary(disputes: Dispute[], nowMs: number) {
  let breaching = 0, resolved = 0, met = 0;
  for (const d of disputes) {
    const s = slaState(d, nowMs);
    if (s.open && s.breached) breaching += 1;
    if (!s.open && s.met !== undefined && d.status === "resolved") { resolved += 1; if (s.met) met += 1; }
  }
  return { breaching, compliancePct: resolved > 0 ? Math.round((met / resolved) * 100) : null };
}
